/* eslint-disable max-lines-per-function */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { PageContext } from '@/types/pageContext';

import type { SelectedProductInfo } from './ChatSidebarContextDef';
import {
  ChatSidebarContext,
  ChatSidebarMode,
  ConversationContext,
} from './ChatSidebarContextDef';

export const CHAT_SIDEBAR_STORAGE_KEY = 'stella:chat:state';
export const CHAT_CONVERSATION_STORAGE_KEY = 'stella:chat:currentConversation';
export const CHAT_SIDEBAR_WIDTH_KEY = 'stella:chat:sidebarWidth';
export const CHAT_DRAFT_MESSAGE_KEY = 'stella:chat:draftMessage';
const DRAFT_MESSAGE_DEBOUNCE_MS = 300;

export const MIN_SIDEBAR_WIDTH = 460;
export const MAX_SIDEBAR_WIDTH = 800;
export const DEFAULT_SIDEBAR_WIDTH = 480;

interface ChatSidebarState {
  isOpen: boolean;
  mode: ChatSidebarMode;
}

const loadPersistedState = (): ChatSidebarState => {
  try {
    const stored = sessionStorage.getItem(CHAT_SIDEBAR_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        isOpen: parsed.isOpen ?? false,
        mode: parsed.mode === 'sidebar' ? 'sidebar' : 'floating',
      };
    }
  } catch (error) {
    console.warn('Failed to load chat sidebar state:', error);
  }
  return { isOpen: false, mode: 'floating' };
};

const loadPersistedConversation = (): string | null => {
  try {
    return sessionStorage.getItem(CHAT_CONVERSATION_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to load conversation ID:', error);
    return null;
  }
};

const loadPersistedWidth = (): number => {
  try {
    const stored = sessionStorage.getItem(CHAT_SIDEBAR_WIDTH_KEY);
    if (stored) {
      const width = parseInt(stored, 10);
      if (width >= MIN_SIDEBAR_WIDTH && width <= MAX_SIDEBAR_WIDTH) {
        return width;
      }
    }
  } catch (error) {
    console.warn('Failed to load sidebar width:', error);
  }
  return DEFAULT_SIDEBAR_WIDTH;
};

const loadPersistedDraftMessage = (): string => {
  try {
    return sessionStorage.getItem(CHAT_DRAFT_MESSAGE_KEY) || '';
  } catch (error) {
    console.warn('Failed to load draft message:', error);
    return '';
  }
};

const usePersistState = (state: ChatSidebarState) => {
  useEffect(() => {
    try {
      sessionStorage.setItem(CHAT_SIDEBAR_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to persist chat sidebar state:', error);
    }
  }, [state]);
};

const usePersistConversation = (conversationId: string | null) => {
  useEffect(() => {
    try {
      if (conversationId) {
        sessionStorage.setItem(CHAT_CONVERSATION_STORAGE_KEY, conversationId);
      } else {
        sessionStorage.removeItem(CHAT_CONVERSATION_STORAGE_KEY);
      }
    } catch (error) {
      console.warn('Failed to persist conversation ID:', error);
    }
  }, [conversationId]);
};

const usePersistWidth = (width: number) => {
  useEffect(() => {
    try {
      sessionStorage.setItem(CHAT_SIDEBAR_WIDTH_KEY, width.toString());
    } catch (error) {
      console.warn('Failed to persist sidebar width:', error);
    }
  }, [width]);
};

const usePersistDraftMessage = (draftMessage: string) => {
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        if (draftMessage) {
          sessionStorage.setItem(CHAT_DRAFT_MESSAGE_KEY, draftMessage);
        } else {
          sessionStorage.removeItem(CHAT_DRAFT_MESSAGE_KEY);
        }
      } catch (error) {
        console.warn('Failed to persist draft message:', error);
      }
    }, DRAFT_MESSAGE_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [draftMessage]);
};

const useChatCallbacks = (
  setState: React.Dispatch<React.SetStateAction<ChatSidebarState>>,
  setCurrentConversationIdState: React.Dispatch<
    React.SetStateAction<string | null>
  >,
  setSidebarWidthState: React.Dispatch<React.SetStateAction<number>>,
  setDraftMessageState: React.Dispatch<React.SetStateAction<string>>,
  setPageContextState: React.Dispatch<React.SetStateAction<PageContext | null>>
) => {
  const openChat = useCallback(
    (options?: { mode?: ChatSidebarMode; conversationId?: string }) => {
      if (options?.conversationId) {
        setCurrentConversationIdState(options.conversationId);
      }
      setState((prev) => ({
        isOpen: true,
        mode: options?.mode ?? prev.mode,
      }));
    },
    [setState, setCurrentConversationIdState]
  );

  const closeChat = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, [setState]);

  const toggleChat = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  }, [setState]);

  const switchMode = useCallback(
    (newMode: ChatSidebarMode) => {
      setState((prev) => ({ ...prev, mode: newMode }));
    },
    [setState]
  );

  const setCurrentConversationId = useCallback(
    (id: string | null) => {
      setCurrentConversationIdState(id);
    },
    [setCurrentConversationIdState]
  );

  const setSidebarWidth = useCallback(
    (width: number) => {
      const clampedWidth = Math.max(
        MIN_SIDEBAR_WIDTH,
        Math.min(MAX_SIDEBAR_WIDTH, width)
      );
      setSidebarWidthState(clampedWidth);
    },
    [setSidebarWidthState]
  );

  const setDraftMessage = useCallback(
    (message: string) => {
      setDraftMessageState(message);
    },
    [setDraftMessageState]
  );

  const setPageContext = useCallback(
    (context: PageContext | null) => {
      setPageContextState(context);
    },
    [setPageContextState]
  );

  return {
    openChat,
    closeChat,
    toggleChat,
    switchMode,
    setCurrentConversationId,
    setSidebarWidth,
    setDraftMessage,
    setPageContext,
  };
};

export const ChatSidebarProvider = (props: React.PropsWithChildren) => {
  const [state, setState] = useState<ChatSidebarState>(loadPersistedState);
  const [currentConversationId, setCurrentConversationIdState] = useState<
    string | null
  >(loadPersistedConversation);
  const [sidebarWidth, setSidebarWidthState] =
    useState<number>(loadPersistedWidth);
  const [draftMessage, setDraftMessageState] = useState<string>(
    loadPersistedDraftMessage
  );
  const [pendingNewConversation, setPendingNewConversation] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [pendingSelectedProducts, setPendingSelectedProducts] = useState<
    Record<string, SelectedProductInfo>
  >({});
  const [pageContext, setPageContextState] = useState<PageContext | null>(null);
  const [pendingConversationContext, setPendingConversationContext] =
    useState<ConversationContext>({});
  const [pendingFocusInput, setPendingFocusInput] = useState(false);
  const [pendingConversationTitle, setPendingConversationTitle] = useState<
    string | undefined
  >(undefined);

  usePersistState(state);
  usePersistConversation(currentConversationId);
  usePersistWidth(sidebarWidth);
  usePersistDraftMessage(draftMessage);

  const callbacks = useChatCallbacks(
    setState,
    setCurrentConversationIdState,
    setSidebarWidthState,
    setDraftMessageState,
    setPageContextState
  );

  const openChat = useCallback(
    (options?: {
      mode?: ChatSidebarMode;
      message?: string;
      newConversation?: boolean;
      selectedProducts?: Record<string, SelectedProductInfo>;
      conversationId?: string;
      conversationContext?: ConversationContext;
      focusInput?: boolean;
      conversationTitle?: string;
    }) => {
      callbacks.openChat(options);

      setPendingMessage(options?.message ?? null);

      setPendingNewConversation(options?.newConversation ?? false);
      setPendingSelectedProducts(options?.selectedProducts ?? {});
      setPendingConversationContext(options?.conversationContext ?? {});
      setPendingFocusInput(options?.focusInput ?? false);
      setPendingConversationTitle(options?.conversationTitle);
    },
    [callbacks]
  );

  const clearPendingNewConversation = useCallback(() => {
    setPendingNewConversation(false);
    setPendingMessage(null);
    setPendingSelectedProducts({});
    setPendingConversationContext({});
    setPendingFocusInput(false);
    setPendingConversationTitle(undefined);
  }, []);

  const contextValue = useMemo<ChatSidebarContext>(
    () => ({
      ...callbacks,
      openChat,
      isOpen: state.isOpen,
      mode: state.mode,
      sidebarWidth,
      currentConversationId,
      draftMessage,
      pendingNewConversation,
      pendingMessage,
      pendingSelectedProducts,
      pendingConversationContext,
      pendingConversationTitle,
      clearPendingNewConversation,
      pendingFocusInput,
      setPendingFocusInput,
      pageContext,
    }),
    [
      state,
      sidebarWidth,
      currentConversationId,
      draftMessage,
      callbacks,
      openChat,
      pendingNewConversation,
      pendingMessage,
      pendingSelectedProducts,
      pendingConversationContext,
      pendingConversationTitle,
      clearPendingNewConversation,
      pendingFocusInput,
      pageContext,
    ]
  );

  return (
    <ChatSidebarContext.Provider value={contextValue}>
      {props.children}
    </ChatSidebarContext.Provider>
  );
};
